// ---------------------------------------------------------------------------
// CENTRAL BRAND SETTINGS
// This is the single place to change the brand name, logo, favicon,
// descriptor, tagline, colours, fonts, contact details, service areas,
// age ranges and product-plan names across the entire website.
// Nothing below is hardcoded anywhere else in banners, images or backgrounds.
// ---------------------------------------------------------------------------

export const brand = {
  // --- Identity (temporary, under review) -----------------------------------
  name: "Mothers by Mothers",
  // Temporary logo. Replace these two files when the final asset lands.
  logoUrl: "/brand/logo-ink.png",
  logoUrlCream: "/brand/logo-cream.png",
  wordmark: { line1: "Mothers", line2: "by Mothers" },
  faviconUrl: "/brand/favicon-32.png", // brand logo on coral, generated in public/brand
  descriptor: "Fresh meals for growing children",
  tagline: "It Takes a Village — We’re Yours.",
  storyHeadline: "Built by a Mother, Driven by Love, Backed by Science.",
  beliefStatement: "We do not replace a mother’s care. We help her carry it.",
  legalEntity: "Mothers by Mothers",

  // --- Launch product / collection ------------------------------------------
  product: {
    name: "Mothers by Mothers",
    planLabel: "Children’s Meal Plan",
    description: "A wholesome three-week children’s meal plan",
  },

  // --- Collections -----------------------------------------------------------
  // Lunch leads the site. Breakfast is an add-on collection and can be hidden
  // entirely by setting enabled to false.
  collections: {
    lunch: { enabled: true, label: "Lunch", navLabel: "Menu" },
    breakfast: {
      enabled: true,
      label: "Breakfast",
      navLabel: "Breakfast",
      statusLabel: "Add-on collection · available now",
    },
    dinner: { enabled: false, label: "Dinner", navLabel: "Dinner", statusLabel: "Not yet offered" },
  },

  // --- Design tokens --------------------------------------------------------
  // Brighter, warmer expressions of the approved palette.
  // Brand book: coral + orange on cream, warm near-black ink.
  // Token names are kept stable so the whole site re-skins from here.
  colors: {
    cream: "#FFEDDA",      // page background (brand cream)
    creamDeep: "#FBE2CC",  // alternate section background
    surface: "#FFFFFF",    // cards
    forest: "#F05658",     // brand coral — panels, bars, buttons
    coralInk: "#C43638",   // darker coral for small text and links on light backgrounds
    forestDeep: "#CF2A1B",  // deep brand red (dark sections, footer)
    rust: "#2E2626",       // warm near-black ink
    sage: "#F8C5A8",       // coral blush tint
    sageSoft: "#FFF6EE",   // porcelain peach panels
    amber: "#F68825",      // brand orange — secondary highlight
    honey: "#FFE3C9",      // warm cream accent on red
    cocoa: "#2E2626",      // body text ink
  },
  fonts: {
    heading: '"Playfair Display", Georgia, serif',
    body: '"Barlow", system-ui, sans-serif',
  },

  // --- Contact --------------------------------------------------------------
  contact: {
    name: "Urvashi Parmar",
    email: "hello@mothersbymothers.com",
    phone: "+91 97661 61500",
    whatsapp: "919766161500",
    addressLine: "Pune, Maharashtra, India",
    hours: "Monday to Saturday, 9:00 am – 7:00 pm IST",
    fssai: "FSSAI licence number — to be confirmed",
    gstin: "GSTIN — to be confirmed",
  },
  social: [
    { label: "Instagram", href: "#" },
    { label: "Facebook", href: "#" },
    { label: "LinkedIn", href: "#" },
  ],

  // --- Service areas & age ranges -------------------------------------------
  serviceAreas: {
    cities: ["Pune"],
    launchNote: "Now delivering across selected Pune areas. New areas are added in stages.",
  },
  ageRanges: [
    { id: "2-4", label: "Ages 2–4", portion: "2 to 4" },
    { id: "5-7", label: "Ages 5–7", portion: "5 to 7" },
    { id: "8-10", label: "Ages 8–10", portion: "8 to 10" },
    { id: "11-plus", label: "Ages 11+", portion: "11+" },
  ],

  // --- Announcement bar -----------------------------------------------------
  announcement: {
    enabled: true,
    // Rotating offer / news messages. Add, remove or reorder freely — the bar
    // fades between them. A single entry simply stays put.
    messages: [
      "Fresh breakfasts & lunches, delivered daily in Pune.",
      "Breakfast, lunch or both — weekly and monthly plans.",
      "Home or school delivery across selected Pune areas.",
    ],
    ctaLabel: "Start your plan",
    ctaHref: "/get-started",
  },

  // --- Plans -----------------------------------------------------------------
  // Plans are a matrix: meal type (breakfast / lunch / both) × duration
  // (weekly / monthly). Prices for all six combinations live in one place below
  // and are intentionally left null until they are confirmed.
  mealPlans: {
    types: [
      {
        id: "breakfast",
        label: "Breakfast",
        name: "Breakfast Plan",
        mealsPerWeek: 5,
        rotationCount: 10,
        summary:
          "Five breakfasts a week, rotating through our traditional and power-bowl breakfast collection.",
        collection: "breakfast",
        deliveryModes: ["home", "school"],
        visible: true,
        features: [
          "5 breakfasts per week",
          "Rotating from 10 breakfast dishes",
          "Age-based portions for 2–4, 5–7, 8–10 and 11+",
          "Parent note and child note with every meal",
          "Dietary and allergy profile",
          "Home or school delivery where available",
        ],
      },
      {
        id: "lunch",
        label: "Lunch",
        name: "Lunch Plan",
        mealsPerWeek: 5,
        rotationCount: 15,
        summary:
          "Five lunches a week from our three-week rotating menu.",
        collection: "lunch",
        deliveryModes: ["home", "school"],
        visible: true,
        featured: true,
        features: [
          "5 lunches per week",
          "Rotating three-week menu of 15 lunches",
          "Age-based portions for 2–4, 5–7, 8–10 and 11+",
          "Parent note and child note with every meal",
          "Dietary and allergy profile",
          "Home or school delivery where available",
        ],
      },
      {
        id: "breakfast-lunch",
        label: "Breakfast + Lunch",
        name: "Breakfast & Lunch Plan",
        mealsPerWeek: 10,
        rotationCount: 25,
        summary:
          "Both meals handled — five breakfasts and five lunches a week, on one schedule.",
        collection: "both",
        deliveryModes: ["home", "school"],
        visible: true,
        badge: "Best value",
        features: [
          "5 breakfasts and 5 lunches per week",
          "25 dishes in rotation across both collections",
          "Age-based portions for 2–4, 5–7, 8–10 and 11+",
          "Parent note and child note with every meal",
          "Dietary and allergy profile",
          "Home or school delivery where available",
          "Priority support on WhatsApp",
        ],
      },
    ],
    durations: [
      {
        id: "weekly",
        label: "Weekly",
        shortLabel: "1 week",
        weeks: 1,
        cadence: "week",
        cadenceNote: "Billed weekly",
        renewal: "Renews weekly until you pause or cancel.",
        blurb: "One week to try it properly. Continue, change or stop after that.",
      },
      {
        id: "monthly",
        label: "Monthly",
        shortLabel: "Full 3-week cycle",
        weeks: 3,
        cadence: "cycle",
        cadenceNote: "Billed per cycle",
        renewal: "Renews at the start of each new cycle until you pause or cancel.",
        blurb: "The complete rotation, so no meal repeats within the cycle.",
        featured: true,
      },
    ],
    // One place for every price. Set a number to publish it.
    prices: {
      "breakfast-weekly": null,
      "breakfast-monthly": null,
      "lunch-weekly": null,
      "lunch-monthly": null,
      "breakfast-lunch-weekly": null,
      "breakfast-lunch-monthly": null,
    },
    deliveryFees: {
      "breakfast-weekly": null,
      "breakfast-monthly": null,
      "lunch-weekly": null,
      "lunch-monthly": null,
      "breakfast-lunch-weekly": null,
      "breakfast-lunch-monthly": null,
    },
    // Shown wherever a price is not yet published. Replace `prices` values with
    // numbers to publish real pricing.
    pricePlaceholder: "Contact us for pricing",
    priceNote: "Tell us your child’s plan and your area and we confirm your pricing and start date within one working day. You are never charged on this site.",
    defaultType: "lunch",
    defaultDuration: "monthly",
  },

  // Optional one-off trial, hidden until activated.
  trial: {
    id: "trial",
    name: "Trial Meal",
    summary: "A single meal so your child can try the menu before subscribing.",
    price: null,
    priceNote: "Not available yet.",
    visible: false,
    features: [
      "One meal or limited trial",
      "Breakfast or lunch",
      "Selected delivery areas only",
      "Age-based portion",
    ],
    cta: "Try a Meal",
  },

  // --- Payments -------------------------------------------------------------
  payments: {
    provider: "Razorpay",
    live: false,
    note:
      "Your order is confirmed by our team on WhatsApp and payment is collected when your start date is agreed. You are never charged on this website.",
  },

  // --- Mothers Supporting Mothers programme ---------------------------------
  programme: {
    statusLabel: "Programme in development",
    active: false,
  },

  // --- Trust / food claims --------------------------------------------------
  // Each claim is published only when `published` is true. Claims that need
  // documented evidence are kept unpublished on purpose.
  claims: [
    { id: "nutritionist-developed", text: "Recipes developed with expert paediatric nutritionists", published: true, evidence: "Founder statement — nutritionist names, qualifications and written consent to be filed before launch" },
    { id: "no-premixes", text: "Freshly cooked — never pre-mixes or processed shelf-stable products", published: true, evidence: "Recipe and kitchen specification" },
    { id: "fresh-prep", text: "Freshly prepared to the confirmed kitchen schedule", published: true, evidence: "Operational schedule to be confirmed" },
    { id: "gentle-spice", text: "Gentle Indian flavours, no unnecessarily heavy spice", published: true, evidence: "Recipe specification" },
    { id: "clear-ingredients", text: "Clear ingredient information for every dish", published: true, evidence: "Menu guide" },
    { id: "age-portions", text: "Age-based portions for 2–4, 5–7, 8–10 and 11+", published: true, evidence: "Menu guide" },
    { id: "planned-for-children", text: "Thoughtfully planned for growing children", published: true, evidence: "Menu guide" },
    { id: "pest-chemical-free", text: "Pest-free, chemical-free and fertiliser-free ingredients", published: true, evidence: "Founder standard — supplier certificates to be collected before launch" },
    { id: "traditional-methods", text: "Cooked with pure ghee, using traditional Indian methods", published: true, evidence: "Recipe specification" },
    { id: "a2-ghee", text: "A2 cow ghee", published: true, evidence: "Supplier confirmation to be filed" },
    { id: "organic-jaggery", text: "Organic jaggery instead of refined white sugar", published: true, evidence: "Supplier certification to be filed" },
    { id: "parent-note", text: "A parent note with every meal — ingredients and nutritional value", published: true, evidence: "Standard operating procedure" },
    { id: "kid-note", text: "A fun note for the child explaining what the meal does for them", published: true, evidence: "Standard operating procedure" },
    { id: "child-friendly-presentation", text: "Presented to look engaging and child-friendly", published: true, evidence: "Packing standard" },
    { id: "plastic-free", text: "100% plastic-free, biodegradable packaging", published: true, evidence: "Packaging specification" },
    { id: "no-artificial-colours", text: "No artificial food colours", published: true, evidence: "Recipe specification" },
    { id: "no-refined-sugar", text: "No refined white sugar", published: false, evidence: "Recipe audit pending — honey and date syrup appear on the breakfast menu" },
    { id: "preservative-free", text: "No preservatives", published: true, evidence: "Recipe specification — nothing is added to extend shelf life; meals are cooked and delivered the same day" },
    { id: "cold-pressed-oils", text: "Cold-pressed oils", published: false, evidence: "Supplier confirmation pending" },
    { id: "calorie-balanced", text: "Calorie-balanced meals", published: false, evidence: "Nutritionist sign-off pending" },
    { id: "paediatrician", text: "Paediatrician approved", published: false, evidence: "Requires name, qualifications, written approval and permission" },
  ],

  // --- SEO ------------------------------------------------------------------
  seo: {
    titleTemplate: "%s | Mothers by Mothers",
    defaultTitle: "Mothers by Mothers — Fresh meals for growing children",
    description:
      "A children’s lunch subscription in Pune. A rotating three-week menu of fresh, familiar Indian meals for growing children with clear ingredients, age-based portions and weekly or monthly plans.",
    keywords: [
      "children’s meal service Pune",
      "kids lunch subscription Pune",
      "healthy lunch delivery for children Pune",
      "school lunch delivery Pune",
      "fresh children’s meals",
      "kids tiffin service Pune",
      "weekly kids meal plan",
      "monthly children’s lunch plan",
      "child-friendly Indian meals",
      "meal subscription for working parents",
    ],
    socialImage: "/brand/og-share.png",
    canonicalBase: process.env.REACT_APP_SITE_URL || "",
    indexable: true,
  },

  // --- Analytics placeholders (no IDs configured yet) -----------------------
  analytics: {
    googleAnalyticsId: "",
    metaPixelId: "",
    googleAdsConversionId: "",
  },

  // --- Downloads ------------------------------------------------------------
  downloads: {
    menuGuidePdf: null, // replace with a URL to enable the download button
  },
};

export const activeClaims = () => brand.claims.filter((c) => c.published);

export default brand;
